// const URL = "https://dev--indian-states-v2.netlify.app/feed.json"
const URL = "https://indianstates.csis.org/feed.json"

// Takes in the URL from IndianStates and handles filtering
// and parsing the data
export default function getData(messageFromParent) {
  if (messageFromParent === '' || messageFromParent == undefined) return []
  /* use `.org` in prod or if hardcoding url in dev*/
  // const path = messageFromParent.split(".org")[1]

  /* use `4000` in dev if you're getting message from 
  iframe and running indianStates locally */
  // const path = messageFromParent.split("4000")[1]
  
  /* use `.app` in dev if you're getting a message from
  an iframe on the netlify preview site */
  // const path = messageFromParent.split(".app")[1]
  const path = messageFromParent.split(".org")[1]

  /* parse message from parent to get data filters*/
  const pathParts = path.split("/")
  const filterCategory = pathParts[1]
  const filterValue = pathParts[2] ? pathParts[2].toLowerCase() : ""
  const subsector_filter = getSubsector(messageFromParent)

  /*get data from /feed.json and parse it*/
  const dataPromise = fetch(URL)
    .then((response) => response.json())
    .then((res) => {
      /* filter data using parsed message from parent */
      const filteredData = filterData(res.items, filterCategory, filterValue)

      // handles data for each individual item
      const data = filteredData.map((item, index) => {
        let sources = item.primary_sources
        let transformedSources = sources.map((source) => {
          return [source.url || "", source.name || ""]
        })
        let weeklyOrQuarterly = classifyItem(item)
        let featured = isFeatured(item)
        let states = standardizeStateNames(item.states)
        const modifiedSectors = item.sectors.map((sector) =>
          sector.replace(/\band\b/g, "&")
        )
        const modifiedSubsectors = item.subsectors.map((subsector) =>
          subsector.replace(/\band\b/g, "&")
        )

        return {
          id: index,
          timelineEvent: {
            headline: item.headline,
            url: item.url,
            content: item.content,
            sources: transformedSources,
            is_featured: featured,
            weeklyOrQuarterly: weeklyOrQuarterly,
            authors: item.author
          },
          sectors: modifiedSectors,
          subsectors: modifiedSubsectors,
          states: states,
          type: item.resource_type,
          date_string: item.date,
          date_month: item.date_month,
          date_day: item.date_day,
          date_year: item.date_year,
          date: "",
        }
      })

      const dates = createAndAssignDateObjects(data)
      // make sure dropdowns only have items available in the filtered data
      const setsOfData = extractUniqueSets(filteredData)

      // supserset that includes the items (data: data) and other info needed
      return {
        data: data,
        dates: dates,
        type: setsOfData.uniqueResourceTypes,
        sectors: setsOfData.uniqueSectors,
        subsectors: setsOfData.uniqueSubsectors,
        states: setsOfData.uniqueStates,
        years: setsOfData.uniqueYears,
        subsector_filter: subsector_filter,
        filterCategory: filterCategory,
        filterValue: filterValue,
      }
    })
  return dataPromise
}

// Takes info from URL and returns data with those
// filters already applied. The subsector is handled
// in Options.svelte where the dropdowns are. It also 
// filters out "newsletter" items from everything
// except when the URL is /newsletter.
function filterData(items, category, filterValue) {
  // Normalize the filter value to match the data -- handles
  // hyphenated values from the URL
  let normalizedFilterValue = normalizeSectorsAndStates(filterValue);

  // Initially filter out items with resource_type "Goal"
  let filteredItems = items.filter((item) => item.resource_type !== "Goal");

  // For categories other than "newsletter", also filter out items with resource_type "Newsletter"
  if (category !== "newsletter") {
    filteredItems = filteredItems.filter((item) => item.resource_type !== "Newsletter");
  }

  switch (category) {
    case "states":
      return normalizedFilterValue
        ? filteredItems.filter((item) =>
            item.states
              .map((state) => state.toLowerCase())
              .includes(normalizedFilterValue)
          )
        : filteredItems.filter((item) => item.states.length > 0);
    case "sectors":
      return normalizedFilterValue
        ? filteredItems.filter((item) =>
            item.sectors
              .map((sector) => sector.toLowerCase())
              .includes(normalizedFilterValue)
          )
        : filteredItems.filter((item) => item.sectors.length > 0);
    case "newsletter":
      // For "newsletter", include items with resource_type "Newsletter"
      // This is now effectively handled by the initial filter condition, so just return the filteredItems
      return filteredItems;
    case "resources":
      return filteredItems.filter((item) => item.resource_type === "Resource");
    case "articles":
      return filteredItems.filter((item) => item.resource_type === "Article");
    default:
      return filteredItems;
  }
}

// Extracts the subsector from the URL if present
function getSubsector(url) {
  const subsectorPrefix = "#subsector="
  const subsectorIndex = url.indexOf(subsectorPrefix)

  if (subsectorIndex !== -1) {
    // Extract the subsector from the URL
    return url.substring(subsectorIndex + subsectorPrefix.length)
  }

  // Return null or an empty string if no subsector is found
  return null
}

// Takes array of data items, returns an object with unique sets
// These are needed for the filter dropdowns
function extractUniqueSets(data) {
  const sectorSet = new Set()
  const subsectorSet = new Set()
  const stateSet = new Set()
  const resourceTypeSet = new Set()
  const yearSet = new Set()

  data.forEach((item) => {
    item.sectors.forEach((sector) => {
      const modifiedSector = sector.replace(/\band\b/g, "&")
      sectorSet.add(modifiedSector)
    })

    item.subsectors.forEach((subsector) => {
      const modifiedSubsector = subsector.replace(/\band\b/g, "&").trim()
      subsectorSet.add(modifiedSubsector)
    })

    const standardizedStates = standardizeStateNames(item.states)
    standardizedStates.forEach((state) => stateSet.add(state))

    // Check if the resource_type is "goal" before adding it to the set
    if (item.resource_type !== "Goal") {
      resourceTypeSet.add(item.resource_type);
    }

    yearSet.add(item.date_year)
  })

  return {
    uniqueSectors: Array.from(sectorSet).sort(),
    uniqueSubsectors: Array.from(subsectorSet).sort(),
    uniqueStates: Array.from(stateSet).sort(),
    uniqueResourceTypes: Array.from(resourceTypeSet).sort(),
    uniqueYears: Array.from(yearSet), // Convert yearSet to an array
  }
}

// Needed to sort by date later
function createAndAssignDateObjects(array) {
  let dates = []
  let date_string = []

  for (let i = 0; i < array.length; i++) {
    let date = array[i].date_string

    let dateObject = new Date(date)
    array[i].date = dateObject

    if (!date_string.includes(array[i].date_string)) {
      date_string.push(array[i].date_string)
      dates.push(dateObject)
    }
  }

  return dates
}

// Flag if item is weekly or quarterly
function classifyItem(item) {
  // Initialize the flag as an empty string
  let weeklyOrQuarterly = ""

  if (item.tags.includes("States Weekly")) {
    weeklyOrQuarterly = "STATES WEEKLY"
  } else if (item.tags.includes("States Quarterly Updates")) {
    weeklyOrQuarterly = "STATES QUARTERLY"
  } else if (
    item.headline.includes("States Weekly") ||
    item.headline.includes("States Update")
  ) {
    weeklyOrQuarterly = "STATES WEEKLY"
  }

  return weeklyOrQuarterly
}

function isFeatured(item) {
  let isFeatured = ""

  if (item.is_featured) {
    isFeatured = "FEATURED"
  }

  return isFeatured
}

// Map state names to match the names in the map - fix for
// mistakes in data (only ~5-10 of 4000+ items)
function standardizeStateNames(states) {
  const stateNameMapping = {
    "New Delhi": "Delhi",
    "Jammu and Kashmir": "Union Territory of Jammu and Kashmir",
    "Center-State relations": "Center-State Relations",
    "Center-state Relations": "Center-State Relations",
    Karna: "Karnataka",
  }

  return states.map((state) => stateNameMapping[state] || state)
}

// Normalize sectors and states from URL to match the data
function normalizeSectorsAndStates(value) {
  // Define a mapping for special case replacements
  const specialCases = {
    "center-state-relations": "center-state relations",
    "water-sanitation": "water & sanitation",
    "land-labor-laws": "land & labor laws",
  }

  // Check if the value is a special case and return the mapped string
  if (specialCases[value]) {
    return specialCases[value]
  }

  // For all other values, apply the original transformation
  return value
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
    .replace(/\band\b/g, "&")
    .toLowerCase()
}
